import { MessageCircle, ShoppingCart } from "lucide-react";

import { Clock } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { AutoReply } from "@/types/fb-type";

interface AutomateResponseListProps {
    autoReplies: AutoReply[];
    onSelect?: (item: AutoReply) => void;
}

export function AutomateResponseList({ autoReplies, onSelect }: AutomateResponseListProps) {
    if (!autoReplies || autoReplies.length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <div className="mb-4 flex justify-center">
                    <MessageCircle size={48} />
                </div>
                <h3 className="text-lg font-medium mb-2">
                    No automated responses yet
                </h3>
                <p className="text-sm">Create automated responses for your Facebook posts</p>
            </div>
        );
    }

    return (
        <ul className="divide-y">
            {autoReplies.map((item) => {
                // Recipient: user who received the auto reply
                const replyTo = item.facebookUser || {
                    firstName: "Unknown",
                    lastName: "",
                    profileImageUrl: null,
                    name: "Unknown",
                };

                const replyToName = (replyTo.name ?? `${replyTo.firstName ?? ""} ${replyTo.lastName ?? ""}`.trim()) || "Unknown";

                const formattedTime = item.timestamp
                    ? new Date(item.timestamp).toLocaleString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        day: "2-digit",
                        month: "short",
                    })
                    : "";

                return (
                    <li
                        key={item.id}
                        className={cn(
                            "flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition"
                        )}
                        onClick={() => onSelect?.(item)}
                    >
                        {/* Avatar = replyTo user */}
                        <Avatar className="h-12 w-12 border bg-gray-50">
                            <AvatarImage
                                src={replyTo.profileImageUrl || ""}
                                alt={replyToName}
                            />
                            <AvatarFallback>
                                {(replyTo.firstName || replyToName).charAt(0)}
                                {replyTo.lastName?.charAt(0)}
                            </AvatarFallback>
                        </Avatar>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                            {/* Top: replyTo name + timestamp */}
                            <div className="flex justify-between items-center mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-medium text-sm truncate">
                                        {replyToName}
                                    </span>
                                    {/* System label */}
                                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 shrink-0">
                                        System auto reply
                                    </span>
                                </div>

                                <div className="text-[10px] text-gray-400 flex items-center gap-1 shrink-0">
                                    <Clock size={12} /> {formattedTime}
                                </div>
                            </div>

                            {/* Message content */}
                            <div className="text-xs text-gray-600 truncate mb-1">
                                {item.content}
                            </div>

                            {/* Optional: page user id / fb user id */}
                            <div className="text-[11px] text-gray-400 truncate">
                                To: {item.pageUserId || replyTo.id || "N/A"}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}